import React, { useCallback, useEffect, useRef, useState } from "react";

import { useParams } from "react-router-dom";

import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import { EventContentArg } from "@fullcalendar/core";
import { AnnotationIcon, ClipboardListIcon, HomeIcon, MapIcon, NewspaperIcon, PhotographIcon, TrashIcon } from "@heroicons/react/outline";

import LoadingWheel from "../Shared/LoadingWheel";
import useQSBuilder from "../../hooks/useQSBuilder";
import FacebookIcon from "../../assests/facebook.png";
import useApiHelper from "../../services/useApiHelper";
import InstagramIcon from "../../assests/instagram.png";
import useSocialSchedules from "../../data/useSocialSchedules";
import { ISocialPost } from './../../models/social/SocialPost';
import { ISocialSchedule } from './../../models/social/SocialSchedule';
import { useSocialPostMutations } from "../../data/social/useSocialPostMutations";
import { TriggerType, TriggerTypeNames } from './../../models/social/SocialTrigger';
import SocialPostPreviewModal from './SocialPostPreviewModal';
import useUserContext from "../../contexts/UserContext";

function SocialPostEventCard({ eventInfo, onDelete }: { eventInfo: EventContentArg, onDelete: (e: React.MouseEvent<HTMLButtonElement>, postId: string) => void }) {
  const getIconForTriggerType = (triggerType: TriggerType) => {
    switch (triggerType) {
      case TriggerType.Properties:
        return <HomeIcon className="w-5 h-5 text-etpink-600 mr-2" />;
      case TriggerType.BlogPosts:
      case TriggerType.News:
        return <NewspaperIcon className="w-5 h-5 text-etpink-600 mr-2" />;
      case TriggerType.Evergreen:
        return <MapIcon className="w-5 h-5 text-etpink-600 mr-2" />;
      case TriggerType.Infographics:
        return <PhotographIcon className="w-5 h-5 text-etpink-600 mr-2" />;
      case TriggerType.Reviews:
        return <AnnotationIcon className="w-5 h-5 text-etpink-600 mr-2" />;
      case TriggerType.OneOff:
        return <ClipboardListIcon className="w-5 h-5 text-etpink-600 mr-2" />;
      default:
        return <div className="w-5 h-5 mr-2" />;
    }
  }

  const getTextForTriggerType = (triggerType: TriggerType) => {
    switch (triggerType) {
      case TriggerType.Properties:
        return "Property Post";
      case TriggerType.BlogPosts:
      case TriggerType.News:
        return "News Post";
      case TriggerType.Evergreen:
        return "Evergreen Post";
      case TriggerType.Infographics:
        return "Infographic Post";
      case TriggerType.Reviews:
        return "Review Post";
      case TriggerType.OneOff:
        return "One Off Post";
      default:
        return "Unknown Trigger";
    }
  }

  return <div className="w-full bg-white rounded shadow">
    <div className="flex flex-row items-center font-semibold p-2 bg-gray-200">
      {getIconForTriggerType(eventInfo.event.extendedProps.triggerType)}
      <p className="-mb-0.5 text-etpink-600">{getTextForTriggerType(eventInfo.event.extendedProps.triggerType)}</p>
    </div>
    <div className="my-1 px-2 flex flex-row items-center justify-between">
      {eventInfo.event.extendedProps.triggerType === TriggerType.News ?
        <img src={FacebookIcon} className="w-7 h-7 shadow-sm bg-white rounded-full p-0.5 " alt="Facebook Icon" />
        :
        <div className="flex flex-row items-center">
          <img src={InstagramIcon} className="w-6 h-6 shadow-sm bg-white rounded-full p-0.5" alt="Instagram Icon" />
          <img src={FacebookIcon} className="w-7 h-7 shadow-sm bg-white rounded-full p-0.5 -ml-2.5" alt="Facebook Icon" />
        </div>
      }
      <b className="text-etpink-600">{eventInfo.timeText}</b>
    </div>
    <div
      className="m-2 mt-0 p-1 border rounded-sm line-clamp-3 whitespace-normal"
    >
      {eventInfo.event.extendedProps.message}
    </div>
    {eventInfo.event.extendedProps.deleteEnable &&
      <div className="flex p-2 bg-gray-100">
        <button onClick={(e) => onDelete(e, eventInfo.event.extendedProps.id)}>
          <TrashIcon className="w-5 h-5 text-red-600 hover:text-red-800" />
        </button>
      </div>
    }
  </div>
}

export default function SocialPostCalendar() {
  const { loading } = useUserContext();
  const { get } = useApiHelper();
  const buildQS = useQSBuilder()
  const { agencyId } = useParams();
  const { remove } = useSocialPostMutations(agencyId!);
  const { socialSchedules } = useSocialSchedules(agencyId!);
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [loadingEvents, setLoadingEvents] = useState<boolean>(true);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const calendarRef = useRef<any>(null);

  useEffect(() => {
    document.title = "EstateTrack Portal | Social Post Calendar";
  }, []);

  const fetchCalendarEvents = useCallback(async (startISO: string, endISO: string) => {
    setLoadingEvents(true);

    try {
      const res = await get(`/socialpost/${agencyId}${buildQS({ start: startISO, end: endISO })}`);

      const events = res.data.map((post: ISocialPost) => {
        if (!post.socialScheduleId) {
          return {
            title: 'One off Post',
            date: post.isVirtual ? post.virtualPublishOn : post.postedOn ? post.postedOn : post.postData.publishOn,
            url: post.linkUrl,
            extendedProps: {
              id: post.id,
              deleteEnable: post.isVirtual ? false : post.postedOn == null ? true : false,
              triggerType: TriggerType.OneOff,
              message: post.message,
              postData: post.postData
            }
          }
        } else {
          const schedule = socialSchedules.data.find((s: any) => s.id === post.socialScheduleId) as unknown as ISocialSchedule;

          return {
            title: `${TriggerTypeNames[schedule.trigger.triggerType]}`,
            date: post.isVirtual ? post.virtualPublishOn : post.postedOn ? post.postedOn : post.postData.publishOn,
            url: post.linkUrl,
            extendedProps: {
              id: post.id,
              deleteEnable: post.isVirtual ? false : post.postedOn == null ? true : false,
              triggerType: schedule.trigger.triggerType,
              message: post.message,
              postData: post.postData
            }
          }

        }
      }).filter((e: any) => e !== null);

      setCalendarEvents(events);
    } catch (e: any) {
      setCalendarEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, [get, agencyId, buildQS, socialSchedules.data]);

  const handleDeleteEvent = useCallback((e: React.MouseEvent<HTMLButtonElement>, postId: string) => {
    e.stopPropagation();
    e.preventDefault();

    remove.mutate(postId);

    fetchCalendarEvents(
      calendarRef.current.getApi().view.activeStart.toISOString(),
      calendarRef.current.getApi().view.activeEnd.toISOString()
    );
  }, [remove, fetchCalendarEvents]);

  // Initial fetch for current month to avoid flicker on first render
  useEffect(() => {
    if (socialSchedules.isLoading) return;

    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString();

    fetchCalendarEvents(start, end);
  }, [socialSchedules.isLoading, fetchCalendarEvents]);

  const handleDatesSet = useCallback((fetchInfo: any) => {
    fetchCalendarEvents(fetchInfo.start.toISOString(), fetchInfo.end.toISOString());
  }, [fetchCalendarEvents]);

  const handleEventContent = useCallback((eventInfo: EventContentArg) => <SocialPostEventCard eventInfo={eventInfo} onDelete={handleDeleteEvent} />, [handleDeleteEvent]);

  if (socialSchedules.isLoading) {
    return <div className="flex items-center justify-center py-8"><LoadingWheel width="w-8" height="h-8" /></div>;
  }

  // If we haven't loaded any calendar events yet, show a loader to avoid flicker
  if (loading && loadingEvents && calendarEvents.length === 0) {
    return <div className="flex items-center justify-center py-8"><LoadingWheel width="w-8" height="h-8" /></div>;
  } else {
    return (
      <div>
        <FullCalendar
          plugins={[dayGridPlugin]}
          initialView="dayGridMonth"
          height="auto"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,dayGridWeek",
          }}
          editable={false}
          // display times like "12:31 PM"
          eventTimeFormat={{ hour: 'numeric', minute: '2-digit', hour12: true }}
          // for week/time grid labels
          slotLabelFormat={{ hour: 'numeric', minute: '2-digit', hour12: true }}
          firstDay={1}
          // events are managed in component state to avoid flicker
          ref={calendarRef}
          events={calendarEvents}
          datesSet={handleDatesSet}
          eventContent={handleEventContent}
          eventClick={(e) => {
            e.jsEvent.preventDefault();
            e.jsEvent.stopPropagation();

            setSelectedEvent(e.event);
            setShowPreview(true);


            // Yes, we actually have to check for the string "null", thanks fullCalendar
            // if (e.event.url && e.event.url !== "" && e.event.url !== "null") {
            //   window.open(e.event.url, "_blank");
            // } else {
            //   setSelectedEvent(e.event);
            //   setShowPreview(true);
            // }
          }}
        />
        <SocialPostPreviewModal
          event={selectedEvent}
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
        />
      </div>
    )
  }
}